<template>
  <div id="townsquare" class="square" :class="{
    public: userPreferences.hideGrim,
    spectator: session.isPlayerOrSpectator,
    vote: votingStore.nomination,
  }">
    <ul class="circle" :class="['size-' + players.length]">
      <Seat v-for="(player, index) in players" :key="player.seatId || index" :player="player" :class="{
        from: Math.max(swap, move, nominate, rochambo) === index,
        swap: swap > -1,
        move: move > -1,
        nominate: nominate > -1,
        rochambo: rochambo > -1,
      }" @trigger="handleTrigger(index, $event)" />
      <div class="animations">
        <div v-for="anim in animationStore.animations" :key="anim.id" class="animation"
          :style="getAnimationStyle(anim)">
          {{ anim.emoji }}
        </div>
      </div>
    </ul>
    <Bluffs @open-role-modal="openRoleModal" />
    <PlayerChat />
    <VoiceChatModal />
    <SideMenu />
    <Npcs />
    <ReminderModal :player-index="selectedPlayer" />
    <RoleModal :player-index="selectedPlayer" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { Bluffs, Npcs, PlayerChat, ReminderModal, RoleModal, Seat, SideMenu, VoiceChatModal } from '@/components';
import {
  useChatStore,
  useGrimoireStore,
  usePlayersStore,
  usePlayersMenuStore,
  useSessionStore,
  useUserPreferencesStore,
  useRochamboStore,
  useVotingStore,
  useAnimationStore,
  useDialogStore,
} from "@/stores";
import type { Animation } from "@/stores/useAnimationStore";
import type { Player } from "@/types";

const chatStore = useChatStore();
const grimoire = useGrimoireStore();
const playersStore = usePlayersStore();
const playersMenuStore = usePlayersMenuStore();
const session = useSessionStore();
const userPreferences = useUserPreferencesStore();
const rochamboStore = useRochamboStore();
const votingStore = useVotingStore();
const animationStore = useAnimationStore();
const dialogStore = useDialogStore();

const players = computed(() => playersStore.players);


const move = ref(-1);
const nominate = ref(-1);
const rochambo = ref(-1);
const selectedPlayer = ref(0);
const swap = ref(-1);

const handleTrigger = (playerIndex: number, event: string | [string] | [string, unknown]) => {
  const [method, params] = Array.isArray(event) ? [event[0], event[1]] : [event, undefined];
  const methodMap: Record<string, Function> = {
    claimSeat,
    cancel,
    openReminderModal,
    openRoleModal,
    removePlayer,
    swapPlayer,
    movePlayer,
    nominatePlayer,
    rochamboPlayer,
  };

  if (typeof methodMap[method] === "function") {
    methodMap[method](playerIndex, params);
  }
};

const claimSeat = (playerIndex: number) => {
  const player = players.value[playerIndex];
  if (player && session.playerId === player.id) {
    session.claimSeat(-1);
  } else {
    session.claimSeat(playerIndex);
  }
};

const openReminderModal = (playerIndex: number) => {
  selectedPlayer.value = playerIndex;
  grimoire.toggleModal("reminder");
};

const openRoleModal = (playerIndex: number) => {
  const player = players.value[playerIndex];
  if (session.isPlayerOrSpectator && player && player.role?.team === "traveler") return;
  selectedPlayer.value = playerIndex;
  grimoire.toggleModal("role");
};

const cancel = () => {
  move.value = -1;
  swap.value = -1;
  nominate.value = -1;
  rochambo.value = -1;
};

const removePlayer = async (playerIndex: number) => {
  if (session.isPlayerOrSpectator || votingStore.lockedVote) return;
  if (!playersMenuStore.removePlayer) return;

  const player = players.value[playerIndex];
  if (!player || !(await dialogStore.confirm(`Do you really want to remove ${player.name}?`))) return;

  const nomination = votingStore.nomination;
  if (nomination) {
    const nominator = Number(nomination.nominator);
    const nominee = Number(nomination.nominee);
    if (nominator === playerIndex || nominee === playerIndex) {
      // abort vote if removed player is either nominator or nominee
      votingStore.setNomination(null);
    } else if (
      nominator > playerIndex ||
      nominee > playerIndex
    ) {
      // update nomination array if removed player has lower index
      votingStore.setNomination({
        nominator: nominator > playerIndex ? nominator - 1 : nominator,
        nominee: nominee > playerIndex ? nominee - 1 : nominee,
      });
    }
  }
  rochamboStore.remapSeats((seat) =>
    seat === playerIndex ? -1 : seat > playerIndex ? seat - 1 : seat,
  );
  playersStore.remove(playerIndex);
};

const getSeatPosition = (index: number, total: number) => {
  const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
  const radius = 37;
  const x = radius * Math.cos(angle);
  const y = radius * Math.sin(angle);
  return { x, y };
};

const getAnimationStyle = (anim: Animation) => {
  const total = players.value.length;
  const fromPos = getSeatPosition(anim.from, total);
  const toPos = getSeatPosition(anim.to, total);
  return {
    left: `calc(50% + ${fromPos.x}vmin)`,
    top: `calc(50% + ${fromPos.y}vmin)`,
    '--to-x': `${toPos.x - fromPos.x}vmin`,
    '--to-y': `${toPos.y - fromPos.y}vmin`,
  };
};

const swapPlayer = (from: number, to?: Player) => {
  if (session.isPlayerOrSpectator || votingStore.lockedVote) return;
  if (!playersMenuStore.swapPlayers) return;

  if (to === undefined) {
    cancel();
    swap.value = from;
  } else {
    const swapTo = players.value.indexOf(to);
    const swapFrom = swap.value;
    updateNominationForSwap(swapFrom, swapTo);
    rochamboStore.remapSeats((seat) =>
      seat === swapFrom ? swapTo : seat === swapTo ? swapFrom : seat,
    );
    playersStore.swap([swapFrom, swapTo]);
    cancel();
  }
};

const updateNominationForSwap = (swapFrom: number, swapTo: number) => {
  const currentNomination = votingStore.nomination;
  if (!currentNomination) return;

  let newNominator = Number(currentNomination.nominator);
  let newNominee = Number(currentNomination.nominee);

  if (newNominator === swapFrom) newNominator = swapTo;
  else if (newNominator === swapTo) newNominator = swapFrom;

  if (newNominee === swapFrom) newNominee = swapTo;
  else if (newNominee === swapTo) newNominee = swapFrom;

  if (
    Number(currentNomination.nominator) !== newNominator ||
    Number(currentNomination.nominee) !== newNominee
  ) {
    votingStore.setNomination({
      nominator: newNominator,
      nominee: newNominee
    });
  }
};

const movePlayer = (from: number, to?: Player) => {
  if (session.isPlayerOrSpectator || votingStore.lockedVote) return;
  if (!playersMenuStore.movePlayer) return;

  if (to === undefined) {
    cancel();
    move.value = from;
  } else {
    const moveTo = players.value.indexOf(to);
    const mapIndex = (idx: number) => {
      if (idx === move.value) return moveTo;
      if (idx > move.value && idx <= moveTo) return idx - 1;
      if (idx < move.value && idx >= moveTo) return idx + 1;
      return idx;
    };

    if (votingStore.nomination) {
      // update nomination if it is affected by the move
      const currentNomination = votingStore.nomination;

      const newNominator = mapIndex(Number(currentNomination.nominator));
      const newNominee = mapIndex(Number(currentNomination.nominee));

      if (
        Number(currentNomination.nominator) !== newNominator ||
        Number(currentNomination.nominee) !== newNominee
      ) {
        votingStore.setNomination({
          nominator: newNominator,
          nominee: newNominee
        });
      }
    }
    rochamboStore.remapSeats(mapIndex);
    playersStore.move([move.value, moveTo]);
    cancel();
  }
};

// First click picks the challenger, second click picks their opponent
const rochamboPlayer = (from: number, to?: Player) => {
  if (session.isPlayerOrSpectator) return;
  if (!playersMenuStore.rochambo) return;

  if (to === undefined) {
    cancel();
    if (from !== rochambo.value) {
      rochambo.value = from;
    }
  } else {
    const opponent = players.value.indexOf(to);
    if (rochambo.value > -1 && opponent > -1 && rochambo.value !== opponent) {
      rochamboStore.start([rochambo.value, opponent]);
    }
    cancel();
  }
};

const nominatePlayer = (from: number, to?: Player) => {
  if (session.isPlayerOrSpectator || votingStore.lockedVote) return;
  if (to === undefined) {
    cancel();
    if (from !== nominate.value) {
      nominate.value = from;
    }
  } else {
    const nominator = players.value[nominate.value];
    votingStore.setNomination({
      nominator: nominate.value,
      nominee: players.value.indexOf(to)
    });
    if (nominator) {
      chatStore.logEvent("chat.log.nominates", {
        accusator: nominator.name,
        accusee: to.name,
      });
    }
    cancel();
  }
};
</script>

<style lang="scss" src="./TownSquare.scss"></style>
